import React, { useRef, useCallback, useEffect } from "react";
import { useDispatch } from "react-redux";
import { useTracker } from "@dpdgroupuk/react-event-tracker";

import RouteList from "../../../components/LeftMenu/components/RouteComponents/RouteList";
import DriverDetailsDialog from "../../../features/driverDetails";
import ModalControlled from "../../../components/ModalControlled";
import { wrapPromise } from "../../../utils/promise";
import useLocationQueryParams from "../../../hooks/useLocationQueryParams";
import AddNewRouteDialog from "../../../features/addNewRoute";
import { changeRouteStatus } from "../../../store/actions/routes";
import InBuildWarningModal from "../../../components/modals/InBuildWarningModal";
import RevertRouteWarning from "../../../components/modals/RouteStatusWarning";
import { ROUTES } from "../../../constants/analytics";
import RouteStatus from "../../../enums/RouteStatus";
import noop from "../../../utils/noop";

const RouteListContainer = () => {
  const tracker = useTracker();
  const driverDialogRef = useRef();
  const inBuildWarningRef = useRef();
  const revertStatusWarningRef = useRef();
  const addRouteDialogRef = useRef();
  const { routeId, detailVisible } = useLocationQueryParams();
  const filters = useLocationQueryParams({ parseNumbers: true });
  const dispatch = useDispatch();

  const isRouteDetail = !!(routeId && detailVisible);

  useEffect(() => {
    tracker.logEvent(ROUTES.LOAD);
  }, [tracker]);

  const showDriverModal = useCallback((route) => {
    wrapPromise(
      driverDialogRef.current.showModal({
        route,
      })
    );
  }, []);

  const handleRouteStatusChange = useCallback(
    (newRouteStatus, routeId, currentRouteStatus) => {
      const updateStatus = () =>
        dispatch(changeRouteStatus(newRouteStatus, routeId));
      switch (currentRouteStatus) {
        case RouteStatus.IN_BUILD:
          inBuildWarningRef.current.showModal().then(updateStatus, noop);
          break;
        case RouteStatus.DOWNLOADED:
        case RouteStatus.LOADING:
        case RouteStatus.DEPARTED:
          revertStatusWarningRef.current.showModal().then(updateStatus, noop);
          break;
        default:
          updateStatus();
      }

      tracker.logEvent(ROUTES.SELECT_ROUTES_STATUS);
    },
    [tracker, dispatch]
  );

  const handleRouteAddClick = useCallback(() => {
    wrapPromise(addRouteDialogRef.current.showModal());

    tracker.logEvent(ROUTES.CLICK_NEW_ROUTE_ICON);
  }, [tracker]);

  return (
    <>
      <RouteList
        onRouteCodeClick={showDriverModal}
        routeId={routeId}
        filters={filters}
        isRouteDetail={isRouteDetail}
        onStatusChange={handleRouteStatusChange}
        onListAddItemClick={handleRouteAddClick}
      />
      <ModalControlled
        Component={DriverDetailsDialog}
        ref={driverDialogRef}
        renderWhenOpen
      />
      <ModalControlled
        ref={inBuildWarningRef}
        Component={InBuildWarningModal}
      />
      <ModalControlled
        ref={revertStatusWarningRef}
        Component={RevertRouteWarning}
      />
      <ModalControlled
        Component={AddNewRouteDialog}
        ref={addRouteDialogRef}
        renderWhenOpen
      />
    </>
  );
};

export default RouteListContainer;
